import { type GetSubTaskByIdQuery, useGetSubTaskByIdLazyQuery } from '@exam-management/graphql';
import { useEffect, useState } from 'react';

import { type SubtaskDetailsData } from '../../types';

interface TransformQSetReviewSubtaskDetailsDataProps {
  subTask: GetSubTaskByIdQuery['wflGetTaskById']['task'];
}
function transformQSetReviewSubtaskDetailsData(
  props?: Readonly<TransformQSetReviewSubtaskDetailsDataProps>,
): SubtaskDetailsData {
  const { subTask: data } = props ?? {};

  return {
    id: data?.id ?? '',
    code: data?.code ?? '',
    updatedAt: data?.updatedAt ?? new Date(),
    dueDate: data?.dueDate ?? new Date(),
    submittedAt: data?.submittedAt ?? new Date(),
    assignedAt: data?.assignedAt ?? new Date(),
    assignee: {
      id: data?.assignee.id ?? '',
      firstname: data?.assignee.firstname ?? '',
      lastname: data?.assignee.lastname ?? '',
    },
  };
}

export function useSubTaskDetails(subTaskId: string[]) {
  const [getSubTaskDetails] = useGetSubTaskByIdLazyQuery();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<SubtaskDetailsData[]>([]);

  useEffect(() => {
    const fetchSubTaskDetails = async () => {
      setIsLoading(true);
      try {
        if (subTaskId.length > 0) {
          const resultData = await Promise.all(
            subTaskId.map((id) =>
              getSubTaskDetails({ variables: { getTaskByIdInput: { taskId: id } } }),
            ),
          );
          const subTaskDetails = resultData.map((result) => {
            if (!result.data?.wflGetTaskById?.task) {
              throw new Error('Task data is undefined');
            }
            return transformQSetReviewSubtaskDetailsData({
              subTask: result.data?.wflGetTaskById?.task,
            });
          });
          // Sort code by last number after split by '-' in ascending order
          const sortedSubTaskDetails = subTaskDetails.slice().sort((a, b) => {
            const codeAList = a.code.split('-');
            const codeBList = b.code.split('-');
            const codeNumberA = parseInt(codeAList[codeAList.length - 1], 10);
            const codeNumberB = parseInt(codeBList[codeBList.length - 1], 10);
            return codeNumberA - codeNumberB;
          });
          setData(sortedSubTaskDetails);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubTaskDetails();
  }, [subTaskId, getSubTaskDetails]);

  return {
    subTaskDetails: data,
    isLoading: isLoading,
  };
}
